'use client';
import { useTranslations } from 'next-intl';
import { type FC, type PropsWithChildren, type ReactNode, useMemo } from 'react';
import { PlanDialogScene } from '@/app/components/shared/PlanDetailDialog';
import { TaskSubmitButton } from '@/app/components/shared/TaskSubmitButton';
import { toast } from '@/app/components/ui/use-toast';
import { useAdsDialog, useCostCredits, useFreeCreditsTasks, useGlobalModal, useTrials } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { TaskInfo, TaskParams, ToolFormParams } from '@/app/types';
import { getDefaultReportParams, reportEvent } from '@/app/utils';
import { convertToolTask, retryPendingTempUploads } from '@/app/utils/client';
import { usePathname } from '@/i18n/routing';
import { useToolSubmit } from '../_hooks/use-tool-submit';

interface ToolFormSubmitButtonProps {
  maxImageCount?: number;
  className?: string;
  taskType: TaskParams['type'];
  submitValue?: TaskParams;
  textCreditsMeasuredLength?: number;
  creditsText?: string;
  inZeroCreditsTrials?: boolean;
  /**
   * 提交成功回调
   * @returns 返回 'suppressDefaultToast' 时不显示默认 toast，否则显示默认 toast
   */
  onSubmitSuccess?: (task: TaskInfo) => 'suppressDefaultToast' | undefined;
  onBeforeSubmit?: () => boolean | Promise<boolean>;
  onTrialsUpdated?: () => void;
  onCreditsInsufficientClick?: () => void;
  // 用于展示 loading 状态
  processing?: boolean;
  disabled?: boolean;
  // 用于禁用时按钮显示的tooltip
  disabledTip?: ReactNode;
  // 自定义按钮额外的内容
  extra?: ReactNode;
  // 是否展示消耗积分数和积分icon
  displayCredits?: boolean;
  displayOriginalCredits?: boolean;
  // 用于上报事件
  isInspireMe?: boolean;
}

export const ToolFormSubmitButton: FC<PropsWithChildren<ToolFormSubmitButtonProps>> = ({
  taskType,
  submitValue: providedSubmitValue,
  textCreditsMeasuredLength,
  creditsText,
  inZeroCreditsTrials = false,
  children,
  onSubmitSuccess,
  onBeforeSubmit,
  onTrialsUpdated,
  onCreditsInsufficientClick,
  isInspireMe = false,
  processing = false,
  disabled = false,
  disabledTip = '',
  className,
  extra,
  displayCredits = true,
  displayOriginalCredits = true,
}) => {
  const { updateTrialsAsync } = useTrials();
  const t = useTranslations();
  const { open: openPlanDetailDialog } = useGlobalModal('planDetailDialog');
  const { open: openPurchaseCreditsDialog } = useGlobalModal('purchaseCreditsDialog');
  const { open: openLoginDialog } = useGlobalModal('loginDialog');
  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');
  const { tryOpenInsufficientCreditsDialog } = useAdsDialog();
  const { fetchFreeCreditTasks } = useFreeCreditsTasks();

  const { formParams, userInfo, creditsInfo, isTeamSpace } = useStore((state) => {
    let selectedFormParams: ToolFormParams | null = null;

    if (!providedSubmitValue) {
      switch (taskType) {
        case 'face_swap':
          selectedFormParams = state.faceSwapFormParams;
          break;
        case 'remake':
          selectedFormParams = state.remakeFormParams;
          break;
        case 'one_click':
          selectedFormParams = state.oneClickVideoProductionFormParams as unknown as ToolFormParams;
          break;
        case 'music_video':
          selectedFormParams = state.oneClickMvFormParams;
          break;
        case 'lip_sync':
          selectedFormParams = state.lipSyncFormParams;
          break;
        case 'offline_avatar':
          selectedFormParams = state.offlineAvatarFormParams;
          break;
        case 'upscale':
          selectedFormParams = state.upscaleVideoFormParams;
          break;
        case 'upscale_image':
          selectedFormParams = state.upscaleImageFormParams;
          break;
        case 'tts':
          selectedFormParams = state.text2ttsFormParams;
          break;
        case 'style_transfer':
          selectedFormParams = state.styleTransferFormParams;
          break;
        case 'motion_control':
          selectedFormParams = state.motionControlFormParams;
          break;
        default:
          selectedFormParams = state.upscaleVideoFormParams;
      }
    }

    return {
      formParams: selectedFormParams,
      userInfo: state.userInfo,
      creditsInfo: state.creditsInfo,
      isTeamSpace: state.isTeamSpace,
    };
  });

  const isFree = userInfo?.subs_plan === 'free';

  const submitValue = useMemo(() => {
    if (providedSubmitValue) {
      return providedSubmitValue;
    }

    if (!formParams) {
      throw new Error('ToolFormSubmitButton requires submitValue for unsupported task types.');
    }

    return convertToolTask({ taskType, formParams });
  }, [taskType, formParams, providedSubmitValue]);

  const { costCredits, originalCredits } = useCostCredits({
    submitValue,
    useV2ToolTaskApi: taskType === 'music_video' && formParams?.oneClickMvVersion === 'v2',
    inZeroCreditsTrials,
    textCreditsMeasuredLength,
    creditsText,
  });

  const creditsInsufficient = useMemo(() => {
    return !!(costCredits && creditsInfo && creditsInfo.total < costCredits);
  }, [costCredits, creditsInfo]);

  const pathname = usePathname();

  const getReportParams = () => {
    return getDefaultReportParams({
      value: submitValue,
      isInspireMe,
    });
  };

  const { submitTask, submitting } = useToolSubmit({
    taskType,
    useV2ToolTaskApi: taskType === 'music_video' && formParams?.oneClickMvVersion === 'v2',
    isFree,
    onSubmitSuccess: (data) => {
      updateTrialsAsync().then(() => {
        onTrialsUpdated?.();
      });

      fetchFreeCreditTasks();

      const result = onSubmitSuccess?.(data);
      return result;
    },
    getReportParams,
  });

  const handleSubmit = async () => {
    if (!userInfo) {
      openLoginDialog();
      reportEvent('login_pop_exposure', {
        refer_element: 'create - submit',
        refer_page: pathname,
      });
      return;
    }

    const retryResult = await retryPendingTempUploads();
    if (!retryResult.success) {
      toast({ type: 'error', message: t('popup.toast.uploadingError') });
      return;
    }
    if (retryResult.retried) {
      return;
    }

    if (onBeforeSubmit) {
      try {
        if (!(await onBeforeSubmit())) {
          return;
        }
      } catch {
        return;
      }
    }

    submitTask(submitValue, costCredits);
  };

  const handleCreditsInsufficientClick = () => {
    if (onCreditsInsufficientClick) {
      onCreditsInsufficientClick();
      return;
    }

    if (isTeamSpace) {
      openConfirmDialog({
        title: t('popup.dialog.error.teamCreditsInsufficientContactAdmin.title'),
        description: t('popup.dialog.error.teamCreditsInsufficientContactAdmin.content'),
        primaryButtonText: t('popup.dialog.error.confirm'),
      });
      return;
    }

    if (isFree) {
      openPlanDetailDialog({ scene: PlanDialogScene.InsufficientCredits });
      return;
    }

    openPurchaseCreditsDialog({
      showTriggerButton: false,
      onClose: () => {
        tryOpenInsufficientCreditsDialog();
      },
    });
  };

  const isNonPeakMode = submitValue.settings.schedule_mode === 'nopeak';

  return (
    <TaskSubmitButton
      className={className}
      disabled={disabled}
      disabledTip={disabledTip}
      submitting={submitting}
      processing={processing}
      onClick={creditsInsufficient ? handleCreditsInsufficientClick : handleSubmit}
      onDisabledClick={async () => {
        if (!(await retryPendingTempUploads()).success) {
          toast({ type: 'error', message: t('popup.toast.uploadingError') });
        }
      }}
      extra={extra}
      displayCredits={displayCredits}
      costCredits={costCredits}
      originalCredits={displayOriginalCredits ? originalCredits : undefined}
      creditsInsufficient={creditsInsufficient}
      creditsVariant={isNonPeakMode ? 'submitButton' : 'currentColor'}
      creditsColor={isNonPeakMode ? '#0086FF' : undefined}
    >
      {children}
    </TaskSubmitButton>
  );
};

ToolFormSubmitButton.displayName = 'ToolFormSubmitButton';
